/**
 * WatchlistContext — single source of truth for the user's watched symbols.
 * Components anywhere in the app subscribe to know if a stock is starred,
 * and call helpers to add/remove without page reloads.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, apiDelete } from "@/lib/api";

const WatchlistContext = createContext(null);

export const WatchlistProvider = ({ children }) => {
  const [symbols, setSymbols] = useState(() => new Set());
  const [tier, setTier] = useState("basic");
  const [limit, setLimit] = useState(10);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [{ symbols: list }, me] = await Promise.all([
        apiGet("/watchlist/symbols"),
        apiGet("/me"),
      ]);
      setSymbols(new Set(list || []));
      setTier(me?.tier || "basic");
      setLimit(me?.watchlist_limit ?? null);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("cse:watchlist-updated", onChange);
    return () => window.removeEventListener("cse:watchlist-updated", onChange);
  }, [refresh]);

  const isWatched = useCallback((sym) => symbols.has(sym), [symbols]);

  const add = useCallback(async (cseSymbol, body = {}) => {
    const created = await apiPost("/watchlist", { cse_symbol: cseSymbol, ...body });
    setSymbols((prev) => {
      const next = new Set(prev);
      next.add(cseSymbol);
      return next;
    });
    window.dispatchEvent(new Event("cse:watchlist-updated"));
    return created;
  }, []);

  const remove = useCallback(async (cseSymbol) => {
    await apiDelete(`/watchlist/${cseSymbol}`);
    setSymbols((prev) => {
      const next = new Set(prev);
      next.delete(cseSymbol);
      return next;
    });
    window.dispatchEvent(new Event("cse:watchlist-updated"));
  }, []);

  const value = useMemo(
    () => ({ symbols, isWatched, add, remove, refresh, tier, limit, loaded, count: symbols.size }),
    [symbols, isWatched, add, remove, refresh, tier, limit, loaded]
  );

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
};

export const useWatchlist = () => {
  const ctx = useContext(WatchlistContext);
  if (!ctx) throw new Error("useWatchlist must be used within <WatchlistProvider>");
  return ctx;
};
